import { useToast, Flex, Tooltip, Button, Text, Box, Icon } from '@chakra-ui/react';
import { TPictures, useUpdatePictureMutation, TPictureUpdateResponse } from 'core/API/picturesApi';
import { useTranslation } from 'helpers/languages/translations';
import icons from 'shared/icons';
import { usePicturesFilters } from '../ImagesUpload/ImageUploadContainer';

const validationFn = (selectedPicture: TPictures, positions: number[]) => (f: Partial<TPictures>) => {
    const errors = [];

    if (!f.position) {
        errors.push('position');
    }

    if (positions.includes(Number(f.position)) && Number(f.position) !== selectedPicture.position) {
        errors.push('positionExist');
    }

    if (f.retailers_id?.length === 0) {
        errors.push('retailers_id');
    }

    return errors;
};

const DetailForm = ({
    selectedPicture,
    positions,
    readonly,
}: {
    selectedPicture: TPictures;
    positions: number[];
    readonly: boolean;
}) => {
    const toast = useToast();
    const translate = useTranslation();

    const validation = validationFn(selectedPicture, positions);

    const [updatePictureQuery, { isLoading }] = useUpdatePictureMutation();
    const { filters, errors, ean, image_caractersitics, image_type, position, retailers_id } = usePicturesFilters(
        selectedPicture,
        false,
        selectedPicture.isFromApi || readonly,
        validation,
    );

    const handleUpdate = async () => {
        const { isAll, retailers_id, ...options } = filters;

        if (selectedPicture._id) {
            const response: any = (await updatePictureQuery({
                picture_id: selectedPicture._id,
                ...selectedPicture,
                ...options,
                retailers_id: isAll ? [] : retailers_id,
            })) as { data: TPictureUpdateResponse };

            const resp = response.data || response.error;
            const status = resp.data ? 'success' : 'error';

            toast({
                title: translate(`picture.update.${status}`),
                status,
            });
        }
    };

    return (
        <Flex w="400px" direction="column" fontWeight="bold">
            <Text fontSize="lg">{`${translate('pictures.product_name')}:`}</Text>
            <Text mb={3} fontWeight="normal">
                {selectedPicture.product_name || translate('pictures.noLabel')}
            </Text>
            <Box>
                <Text>EAN:</Text>
                {ean}
            </Box>
            <Text textTransform="capitalize">{translate('pictures.format')}:</Text>
            <Flex alignItems="center">
                {image_caractersitics}
                <Tooltip label={translate('pictures.format.description')}>
                    <Icon as={icons.info} boxSize="20px" ml={3} />
                </Tooltip>
            </Flex>
            <Text textTransform="capitalize">{translate('pictures.type')}:</Text>
            <Flex alignItems="center">
                {image_type}
                <Tooltip label={translate('pictures.type.description')}>
                    <Icon as={icons.info} boxSize="20px" ml={3} />
                </Tooltip>
            </Flex>
            <Text textTransform="capitalize">Position:</Text>
            <Flex alignItems="center">
                {position}
                <Tooltip label={translate('pictures.position.description')}>
                    <Icon as={icons.info} boxSize="20px" ml={3} />
                </Tooltip>
            </Flex>
            <Text textTransform="capitalize">{translate('retailers')}:</Text>
            <Flex alignItems="center">
                {retailers_id}
                <Tooltip label={translate('pictures.retailers.description')}>
                    <Icon as={icons.info} boxSize="20px" ml={3} />
                </Tooltip>
            </Flex>
            <Box>
                {!selectedPicture.isFromApi && (
                    <>
                        <Button
                            w="100%"
                            colorScheme={isLoading ? 'gray' : 'blue'}
                            cursor={isLoading ? 'not-allowed' : 'pointer'}
                            onClick={handleUpdate}
                            isDisabled={isLoading || !!errors.length || readonly}
                            mt={3}
                        >
                            {translate('picture.update')}
                        </Button>
                        <Box mt={2} color="red">
                            {errors.map((error) => (
                                <Text fontSize={12}>{translate(`pictures.errors.${error}`)}</Text>
                            ))}
                        </Box>
                    </>
                )}
            </Box>
        </Flex>
    );
};

export default DetailForm;
